import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Loading,
  InlineNotification,
  ToastNotification,
} from '@carbon/react';
import {
  Add,
  SidePanelClose,
  SidePanelOpen,
  Workspace as WorkspaceIcon,
} from '@carbon/react/icons';
import { PageHeader } from 'components/page-header';
import { ActivityFeedPanelOpenButton } from 'components/activity-feed-panel';
import style from './page.module.scss';
import { AreaDetailBody } from './area-detail-body';
import type { AreaGeometry } from 'types/area-geometry';
import { CreateWorkspaceModal } from './create-workspace-modal';
import { AreaWorkspacesDrawer } from './area-workspaces-drawer';
import { AreaWatchButton } from './area-watch-button';
import { useAreaSubscription } from './hooks';
import { activeAreaWorkspaceHrefAtom } from 'pages/area-workspace/atoms';
import { AreaWorkspaceShell } from 'pages/area-workspace/area-workspace-shell';
import { useCurrentArea } from './use-current-area';
import { useCreateAreaWorkspace } from 'pages/area-workspace/use-workspace-writes';
import type { ActionNotice } from 'types/area-workspace';

/** How long a successful change is announced for, matching `SuccessToast`. */
const TOAST_TIMEOUT_MS = 5000;

// Promotion happens inside a workspace and finishes by leaving it, so its
// confirmation arrives here as router state rather than being raised locally.
interface AreaDetailLocationState {
  notice?: ActionNotice;
}

const AreaGeometryShow = () => {
  // The route param is a business_id (the detail is keyed on the business area, not a
  // version): resolve it to the current version and load that version's detail.
  const { id } = useParams<{ id: string }>();
  const location = useLocation<AreaDetailLocationState | undefined>();
  const history = useHistory();
  // A workspace can promote the area, take it back off the map, or be the first one
  // opened on it, and all three change what this page shows — so the copy fetched before
  // the workspace opened is re-read on the way back rather than left to contradict the
  // confirmation.
  const {
    loading,
    error,
    data: areaGeometry,
    refetch: refetchArea,
  } = useCurrentArea<AreaGeometry>(id);

  // The settings rail starts open, as the mock has it.
  const [panelOpen, setPanelOpen] = useState(true);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  // The View Workspaces drawer, listing what the payload already carries.
  const [workspacesOpen, setWorkspacesOpen] = useState(false);
  const subscriptionWrites = useAreaSubscription(
    id,
    areaGeometry?.subscription
  );
  // An open workspace takes over this page rather than being a route of its own, so
  // the URL stays on the area — the same way the pre-drill launcher works.
  const [activeWorkspaceHref, setActiveWorkspaceHref] = useAtom(
    activeAreaWorkspaceHrefAtom
  );
  // What a promotion finished with, whether it happened in the workspace this
  // page is hosting or on an earlier visit that handed it over as router state.
  const [arrived, setArrived] = useState<ActionNotice | null>(null);
  // The POST is version-addressed, and the route param is the business_id — so the
  // create has to wait for the resolver and use the version it found. The fallback
  // only keeps the hook callable while the area loads; nothing can submit before
  // then, because the button that opens the modal renders inside the loaded page.
  const createWorkspace = useCreateAreaWorkspace(
    areaGeometry?.id ?? id,
    areaGeometry?.shared ?? false
  );

  // Clear the last refusal on the way in, so a fresh modal opens on a clean slate.
  const openCreateWorkspace = useCallback(() => {
    createWorkspace.reset();
    setWorkspaceOpen(true);
  }, [createWorkspace]);

  const handleCreateWorkspace = useCallback(
    async (name: string) => {
      const created = await createWorkspace.create(name);
      if (!created) {
        // Refused: the modal stays open and reports why.
        return;
      }
      setWorkspaceOpen(false);
      // The created workspace opens here, in place. Its own href rather than one
      // rebuilt from the id, so the backend keeps saying where it lives.
      setActiveWorkspaceHref(created.href ?? `/workspaces/${created.id}`);
    },
    [createWorkspace, setActiveWorkspaceHref]
  );

  // Closes the workspace and shows whatever it finished with, so promoting from inside
  // it still reports back on this page.
  const handleLeaveWorkspace = useCallback(
    (_areaId: number, notice?: ActionNotice) => {
      setActiveWorkspaceHref(null);
      refetchArea();
      if (notice) {
        setArrived(notice);
      }
    },
    [refetchArea, setActiveWorkspaceHref]
  );

  // Reset on id change: this route reuses the component, and the href atom is
  // module-level, so the previous area's workspace would sit on top of the next one.
  useEffect(() => {
    setArrived(null);
    setActiveWorkspaceHref(null);
  }, [id, setActiveWorkspaceHref]);

  // Take the arriving confirmation into local state and strip it from history, so
  // reloading or coming back to this entry doesn't re-announce work already done.
  const handedOver = location.state?.notice;
  useEffect(() => {
    if (!handedOver) {
      return;
    }
    setArrived(handedOver);
    history.replace(location.pathname);
  }, [handedOver, history, location.pathname]);

  // The local action's own message wins while one is in flight, since it is the
  // more recent thing the user did.
  const notice = arrived;

  // An open workspace takes over the page, as pre-drill does on the well page. Before
  // the load states: it fetches its own copy of the area, so it needn't wait on this.
  if (activeWorkspaceHref) {
    return (
      <AreaWorkspaceShell
        href={activeWorkspaceHref}
        onLeave={handleLeaveWorkspace}
      />
    );
  }

  if (loading) {
    return (
      <div className={style.loadingContainer}>
        <Loading
          description="Loading area geometry data..."
          withOverlay={false}
          small
        />
      </div>
    );
  }

  if (error) {
    return (
      <InlineNotification
        kind="error"
        title="Error"
        subtitle={error}
        lowContrast
      />
    );
  }

  if (!areaGeometry) {
    return (
      <InlineNotification
        kind="error"
        title="Error"
        subtitle="No area geometry data available"
        lowContrast
      />
    );
  }

  const area = areaGeometry;
  const areaName = area.displayName || area.name;
  const existingWorkspaces = area.workspaces?.items ?? [];

  return (
    <div className={style.container}>
      <PageHeader
        title={areaName}
        breadcrumbs={[
          ['/area_geometries', 'Area Geometries'],
          [location.pathname, areaName],
        ]}
        actions={
          <div className={style.headerActions}>
            {/*
              The workspace is where review and promotion belong, so opening one is
              the way onto the map — there is no area-level promote beside it.
            */}
            <Button
              kind="tertiary"
              size="md"
              renderIcon={Add}
              disabled={createWorkspace.saving}
              onClick={openCreateWorkspace}
            >
              New Area Workspace
            </Button>
            {/* Only when there is something to view; the count comes off the payload
                summary the drawer also lists. */}
            {existingWorkspaces.length > 0 && (
              <Button
                kind="tertiary"
                size="md"
                renderIcon={WorkspaceIcon}
                onClick={() => setWorkspacesOpen(true)}
              >
                View Workspaces ({existingWorkspaces.length})
              </Button>
            )}
            {/* Neither promote nor archive lives here any more: both change the area, so
                both happen inside a workspace where the change can be reviewed and
                warned about first. */}
            <Button
              kind="tertiary"
              size="md"
              hasIconOnly
              iconDescription={
                panelOpen ? 'Hide settings panel' : 'Show settings panel'
              }
              tooltipPosition="bottom"
              renderIcon={panelOpen ? SidePanelClose : SidePanelOpen}
              onClick={() => setPanelOpen((open) => !open)}
            />
            <ActivityFeedPanelOpenButton />
            {area.subscription ? (
              <AreaWatchButton
                subscription={area.subscription}
                busy={subscriptionWrites.busy}
                error={subscriptionWrites.error}
                onTopicsChange={subscriptionWrites.setTopics}
                onErrorClose={subscriptionWrites.clearError}
              />
            ) : null}
          </div>
        }
      />

      {/* Below the header actions, not over them: the shared `SuccessToast` sits at the
          top of the viewport, which here lands on the archive button. */}
      {notice && (
        <div className={style.toast}>
          <ToastNotification
            kind="success"
            lowContrast
            title={notice.title}
            subtitle={notice.subtitle}
            timeout={TOAST_TIMEOUT_MS}
            onClose={() => {
              setArrived(null);
              return true;
            }}
          />
        </div>
      )}

      <AreaDetailBody area={area} panelOpen={panelOpen} />

      <CreateWorkspaceModal
        open={workspaceOpen}
        areaName={areaName}
        existingNames={existingWorkspaces.map((item) => item.name)}
        pending={createWorkspace.saving}
        error={createWorkspace.error}
        onClose={() => setWorkspaceOpen(false)}
        onCreate={handleCreateWorkspace}
      />

      <AreaWorkspacesDrawer
        open={workspacesOpen}
        areaName={areaName}
        workspaces={existingWorkspaces}
        onClose={() => setWorkspacesOpen(false)}
        onOpenWorkspace={(workspace) => {
          setWorkspacesOpen(false);
          setActiveWorkspaceHref(workspace.href);
        }}
      />
    </div>
  );
};

export default AreaGeometryShow;
